import React, { useState } from 'react'
import { Grid } from '@mui/material'
import { Link, useNavigate } from 'react-router-dom'
import CircularProgress from '@mui/material/CircularProgress'
import useSignUpUser from '../services/hooks/usePostSignup'
import useLocalStorage from '../../../hooks/useLocalStorage'
import useValidation, { ValidationRule } from '../../../hooks/useValidation'
import TopNav from '../../../components/TopNav/TopNav'
import StyledButton from '../../../components/Button/Button'
import FormInput from '../../../components/Common/FormInput'
import PasswordInput from '../../../components/Common/PasswordInput'
import StyledBgContainer from '../../../components/StyledBgContainer/StyledBgContainer'
import Path from '../../../router/localRoutes'
import EmailIcon from '../../../assets/Message 36.svg'
import './Signup.css'

const Signup = () => {
  const inputValidationRules: Record<string, ValidationRule[]> = {
    email: [
      { required: true, message: 'Please enter your email address.' },
      {
        pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        message: 'Please enter a valid email address.',
      },
    ],
    password: [
      { required: true, message: 'Please enter your password.' },
      {
        pattern: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/,
        message:
          'Password must contain at least 8 characters, including uppercase, lowercase letters, and a number.',
      },
    ],
    confirmPassword: [
      { required: true, message: 'Please confirm your password.' },
      {
        pattern: (value: string, fields: Record<string, string>) =>
          value === fields.password,
        message: 'Passwords do not match.',
      },
    ],
  }

  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')
  const [isChecked, setIsChecked] = useState<boolean>(false)
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [roleValue] = useLocalStorage('role')
  const [, setToken] = useLocalStorage('token')
  const { errors, validateInputs } = useValidation(
    {
      email,
      password,
      confirmPassword,
    },
    inputValidationRules,
  )
  const navigate = useNavigate()

  const { mutate: signUpUser, isPending, isError } = useSignUpUser()

  const onChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
  }

  const onChangePassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value)
  }

  const onChangeConfirmPassword = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setConfirmPassword(event.target.value)
  }

  const onChecked = () => {
    setIsChecked((isChecked) => !isChecked)
  }

  const handleSignUp = async (): Promise<void> => {
    const role = roleValue
    if (!role) {
      navigate(Path.Root)
    }
    if (!validateInputs()) {
      return
    }
    const requestData = {
      email,
      password,
      confirmPassword,
      role,
    }

    signUpUser(requestData, {
      onSuccess: (response) => {
        if (response) {
          setToken(response.data.userTokenSaving.token)
          navigate(Path.AdminDashboard)
        }
      },
      onError: (error) => {
        setErrorMsg(error.message || 'Something went wrong. Please try again.')
      },
    })
  }

  return (
    <StyledBgContainer>
      <Grid
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          color: '#7D7D7D',
        }}
        container
      >
        <TopNav showArrow={true} />
        <Grid
          container
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Grid item xs={12}>
            <h1 className="heading">Sign Up</h1>
          </Grid>
          <div className="signup-form w-[670px] flex flex-col gap-4">
            <Grid item xs={12}>
              <FormInput
                type="email"
                onChange={onChangeEmail}
                value={email}
                label="Email Address"
                startIconComponent={
                  <img
                    src={EmailIcon}
                    alt="Email icon"
                    className="text-white"
                  />
                }
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>
            <Grid item xs={12}>
              <PasswordInput
                type="password"
                onChange={onChangePassword}
                value={password}
                label="Set Password"
                className="password-field"
                error={!!errors.password}
                helperText={errors.password}
              />
            </Grid>
            <Grid item xs={12}>
              <PasswordInput
                type="password"
                onChange={onChangeConfirmPassword}
                value={confirmPassword}
                label="Retype Password"
                className="password-field"
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
              />
            </Grid>
            <Grid item xs={12}>
              <div className="signup-error">
                {isError ? <p className="error1-msg">{errorMsg}</p> : null}
              </div>
              <div className="terms-privacy">
                <input
                  type="checkbox"
                  id="checkbox"
                  onChange={onChecked}
                  value={isChecked ? 1 : 0}
                  className="checkbox"
                />
                <label htmlFor="checkbox">
                  &nbsp;I accept the&nbsp;
                  <Link to={Path.Terms} className="link-style">
                    Terms of Service
                  </Link>
                  &nbsp; and&nbsp;
                  <Link to={Path.PrivacyPolicy} className="link-style">
                    Privacy Policy
                  </Link>
                </label>
              </div>
            </Grid>
          </div>
        </Grid>
        {isPending ? (
          <StyledButton className="submit-signup">
            <CircularProgress className="!text-[#181a21] !text-2xl" />
          </StyledButton>
        ) : (
          <StyledButton
            onClick={handleSignUp}
            disabled={!(email && password && confirmPassword && isChecked)}
            className="submit-signup"
          >
            Sign Up
          </StyledButton>
        )}
        <div className="">
          <p>
            Already have an account? &nbsp;
            <Link to={Path.Signin} className="linkStyle">
              Sign In
            </Link>
          </p>
        </div>
      </Grid>
    </StyledBgContainer>
  )
}

export default Signup
